import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { AppColors } from "../../constants/AppColors";
import { StatisticsChartItemResponse } from "../../models/ResponseModels/StatisticsSummaryResponse";
import {
  formatMoney,
  formatPercent,
  statisticsChartColors,
} from "./statisticsHelpers";

interface AnalyticsPieChartProps {
  title: string;
  items: StatisticsChartItemResponse[];
  emptyText: string;
}

const AnalyticsPieChart = ({
  title,
  items,
  emptyText,
}: AnalyticsPieChartProps) => {
  const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const size = 148;
  const strokeWidth = 18;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 950, mb: 2 }}>{title}</Typography>

        {items.length === 0 || total <= 0 ? (
          <Typography color="text.secondary">{emptyText}</Typography>
        ) : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: "center" }}
          >
            <Box sx={{ position: "relative", width: size, height: size }}>
              <svg width={size} height={size}>
                <circle
                  stroke={AppColors.borderSoft}
                  fill="none"
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  strokeWidth={strokeWidth}
                />

                {items.map((item, index) => {
                  const value = Number(item.value || 0);
                  const dash = (value / total) * circumference;
                  const strokeDasharray = `${dash} ${circumference - dash}`;
                  const strokeDashoffset = -offset;

                  offset += dash;

                  return (
                    <circle
                      key={`${item.label}-${index}`}
                      stroke={
                        statisticsChartColors[
                          index % statisticsChartColors.length
                        ]
                      }
                      fill="none"
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      strokeWidth={strokeWidth}
                      strokeDasharray={strokeDasharray}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                  );
                })}
              </svg>

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ fontWeight: 950 }}>
                  {formatMoney(total, items[0]?.currency)}
                </Typography>

                <Typography sx={{ fontSize: 12, color: AppColors.textMuted }}>
                  total
                </Typography>
              </Box>
            </Box>

            <Stack spacing={1} sx={{ flex: 1, width: "100%" }}>
              {items.map((item, index) => {
                const value = Number(item.value || 0);
                const percentage = total > 0 ? (value / total) * 100 : 0;

                return (
                  <Stack
                    key={`${item.label}-${index}`}
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center", minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor:
                            statisticsChartColors[
                              index % statisticsChartColors.length
                            ],
                          flexShrink: 0,
                        }}
                      />

                      <Typography
                        sx={{ fontWeight: 800 }}
                        noWrap
                        title={item.label}
                      >
                        {item.label}
                      </Typography>
                    </Stack>

                    <Typography color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatPercent(percentage)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsPieChart;
